/**
 * useEmailSyncSettings — the caller's per-mailbox e-mail sync levels + the tenant
 * cap (Berechtigungskonzept V3.0 · Bereich 1), plus the tenant-admin cap read/set.
 *
 * live/mock split mirrors useMailboxConnections: mock mode resolves a fixture so the
 * Postfächer page renders standalone in tests/dev; live mode hits the real endpoints.
 * A query error / 404 (module not landed) degrades to `available: false` — the UI then
 * simply hides the sync-level controls instead of breaking.
 *
 * [Source: apps/web/src/features/mailbox/hooks/useMailboxConnections.ts] (live/mock split)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EmailSyncCapResponse,
  EmailSyncLevel,
  EmailSyncSettingsResponse,
  SetEmailSyncLevelInput,
} from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { apiClient } from '../../../lib/api-client';

/** Mock-mode fixture — cap 3 (all levels allowed), no explicit per-account rows. */
export const MOCK_EMAIL_SYNC_SETTINGS: EmailSyncSettingsResponse = {
  max_email_sync_level: 3,
  default_sync_level: 1,
  mailboxes: [],
};

export interface EmailSyncSettingsState {
  /** false when the endpoint is absent (module not landed) → hide the controls. */
  available: boolean;
  settings: EmailSyncSettingsResponse;
  isLoading: boolean;
}

/** Read GET /email-sync/settings (per-mailbox levels + tenant cap). */
export function useEmailSyncSettings(): EmailSyncSettingsState {
  const live = isLiveData();
  const query = useQuery<EmailSyncSettingsResponse>({
    queryKey: qk.emailSyncSettings(),
    queryFn: () => (live ? apiClient.emailSync.settings() : Promise.resolve(MOCK_EMAIL_SYNC_SETTINGS)),
    retry: false,
    ...(live ? {} : { initialData: MOCK_EMAIL_SYNC_SETTINGS, staleTime: Infinity }),
  });

  return {
    available: !query.isError,
    settings: query.data ?? MOCK_EMAIL_SYNC_SETTINGS,
    isLoading: query.isLoading,
  };
}

/** Set a per-account (or default) sync level → refresh the settings query. */
export function useSetEmailSyncLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetEmailSyncLevelInput) => apiClient.emailSync.setLevel(body),
    onSuccess: (updated) => {
      qc.setQueryData(qk.emailSyncSettings(), updated);
    },
  });
}

/** Read the tenant-admin cap (GET /admin/email-sync-cap). */
export function useEmailSyncCap() {
  const live = isLiveData();
  return useQuery<EmailSyncCapResponse>({
    queryKey: qk.adminEmailSyncCap(),
    queryFn: () =>
      live ? apiClient.admin.emailSyncCap() : Promise.resolve({ max_email_sync_level: 3 }),
    retry: false,
    ...(live ? {} : { initialData: { max_email_sync_level: 3 }, staleTime: Infinity }),
  });
}

/** Set the tenant-admin cap (PUT /admin/email-sync-cap) → refresh cap + settings. */
export function useSetEmailSyncCap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (maxLevel: EmailSyncLevel) => apiClient.admin.setEmailSyncCap(maxLevel),
    onSuccess: (updated) => {
      qc.setQueryData(qk.adminEmailSyncCap(), updated);
      void qc.invalidateQueries({ queryKey: qk.emailSyncSettings() });
    },
  });
}
